import * as Yup from 'yup';
import { useState, useRef, useEffect } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useSnackbar } from 'notistack';
import { Link, Stack, IconButton, InputAdornment, Typography, Checkbox, FormControlLabel } from '@mui/material';

import { LoadingButton } from '@mui/lab';
import { PATH_AUTH, PATH_DASHBOARD } from '../../../routes/paths';
import useAuth from '../../../hooks/useAuth';
import useIsMountedRef from '../../../hooks/useIsMountedRef';
import Iconify from '../../../components/Iconify';
import { FormProvider, RHFTextField, RHFCheckbox } from '../../../components/hook-form';

import ErrorSpan from '../verify-code/ErrorSpan';
import '../register/register_style.css';


// ----------------------------------------------------------------------

export default function LoginForm() {
  const formRef = useRef(null);
  const { login } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const isMountedRef = useIsMountedRef();

  const [showPassword, setShowPassword] = useState(false);

  const [emailName, setEmailName] = useState('');
  const [passwordName, setPasswordName] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [validatecheck, setValidateCheck] = useState(true);
  const loginIn = useRef(false);

  const [nameError, setNameError] = useState({
    emailError: null,
    passwordError: null,
  });

  const LoginSchema = Yup.object().shape({});

  const defaultValues = {
    email: '',
    password: '',
  };

  const methods = useForm({
    resolver: yupResolver(LoginSchema),
    defaultValues,
  });

  const {
    reset,
    setError,
    handleSubmit,
    formState: { errors, isSubmitting },
    er,
  } = methods;
  const checkboxValues = localStorage.getItem('checkboxValue');
  const email = localStorage.getItem('email');
  const password = localStorage.getItem('password');
  useEffect(() => {
    if (checkboxValues === 'checked' && email && password) {
      setRememberMe(checkboxValues);
      setEmailName(email);
      setPasswordName(password);
    } else {
      setRememberMe('');
      setEmailName('');
      setPasswordName('');
    }
  }, []);

  const handleRember = (event) => {
    if (event.target.checked) {
      setRememberMe(event.target.checked);
      localStorage.setItem('checkboxValue', 'checked');
      localStorage.setItem('email', emailName); // email is the user's email address
      localStorage.setItem('password', passwordName); // password is the user's password
    } else {
      localStorage.removeItem('checkboxValue');
      localStorage.removeItem('email');
      localStorage.removeItem('password');
    }
  };

  const onSubmit = async (data) => {
    const userid = window.localStorage.getItem('user_id');
    const accessToken = window.localStorage.getItem('accessToken');
    const usertype = window.localStorage.getItem('user_type');
    let error = {
      emailError: null,
      passwordError: null,
    };
    if (emailName.trim().length === 0) {
      error = { ...error, emailError: 'Email required' };
    }
    if (passwordName.trim().length === 0) {
      error = { ...error, passwordError: 'Password required' };
    }
    setNameError(error);

    if (error.emailError || error.passwordError) {
      return;
    }

    try {
      const result = await login(emailName, passwordName, rememberMe);

      if (!result.success) {
        const id = result.message.user;

        if (!id) {
          enqueueSnackbar(result.message.error, { variant: 'error' });
        } else {
          enqueueSnackbar(
            <p>
              {result.message.error},
              <Link variant="subtitle2" component={RouterLink} to={`${PATH_AUTH.verify}/${id}`}>
                Verify User
              </Link>
            </p>,
            { variant: 'error' }
          );
        }
      } else {
        enqueueSnackbar('Login successful!');

        loginIn.current = true;

        setTimeout(() => {
          navigate(PATH_DASHBOARD.root);
        }, 1000);
      }
    } catch (error) {
      reset();
      if (isMountedRef.current) {
        setError('afterSubmit', error);
      }
    }
  };

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <Stack spacing={3}>
        <RHFTextField
          id="autosuggest-input"
          type="email"
          name="email"
          label="Email address"
          value={emailName}
          onChange={(e) => {
            e.persist();
            setEmailName(e.target.value);
            const selectedValue = document.getElementById('autosuggest-input').value;
            if (selectedValue.length) {
              setNameError({ ...nameError, emailError: '' });
            } else {
              setNameError({ ...nameError, emailError: 'Email required' });
            }
          }}
        />
        {nameError.emailError ? <ErrorSpan error={nameError.emailError} /> : null}

        <RHFTextField
          name="password"
          id="autosuggest-input"
          label="Password"
          type={showPassword ? 'text' : 'password'}
          value={passwordName}
          onChange={(e) => {
            e.persist();
            const selectedValue = document.getElementById('autosuggest-input').value;

            setPasswordName(e.target.value);
            if (selectedValue.length) {
              setNameError({ ...nameError, passwordError: '' });
            } else {
              setNameError({ ...nameError, passwordError: 'Enter Password' });
            }
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                  <Iconify icon={showPassword ? 'eva:eye-fill' : 'eva:eye-off-fill'} />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        {nameError.passwordError ? <ErrorSpan error={nameError.passwordError} /> : null}
      </Stack>

      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ my: 2 }}>
        <FormControlLabel
          control={<Checkbox checked={rememberMe} onChange={handleRember} name="remember" />}
          label="Remember me"
        />
        <Link component={RouterLink} variant="subtitle2" to={PATH_AUTH.resetPassword}>
          Forgot password?
        </Link>
      </Stack>

      <LoadingButton fullWidth size="large" type="submit" variant="contained" loading={isSubmitting}>
        Login
      </LoadingButton>
    </FormProvider>
  );
}
